import fetch from './index'

export type IdpWebKey = 'FG' | 'ST' | 'BF'

export interface IdpOverviewDTO {
  webKey: IdpWebKey
  pv: number
  uv: number
  clicks: number
  dau: number
  wau: number
  mau: number
  trend?: Array<{ day: string; pv: number; uv: number; clicks: number }>
  identityDistribution?: Array<{ name: string; value: number }>
  deviceDistribution?: Array<{ name: string; value: number }>
  eventTypeDistribution?: Array<{ name: string; value: number }>
}

export interface IdpRealtimeEventDTO {
  eventId: string
  eventTime: number
  eventType: string
  webKey: IdpWebKey
  deviceId: string
  sessionId: string
  customerId?: string
  deviceType?: string
  pageId?: number
  pageUrl?: string
  elementId?: number
  elementText?: string
  referrer?: string
  scrollDepth?: number
  engagementMs?: number
  props?: Record<string, any>
}

export interface IdpSessionDetailDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
  events?: IdpRealtimeEventDTO[]
}

export interface IdpSessionSummaryDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
}

export interface IdpSessionListDTO {
  webKey: IdpWebKey
  cursor?: string
  nextCursor?: string
  pageSize?: number
  sessions?: IdpSessionSummaryDTO[]
  detail?: IdpSessionDetailDTO
}

export interface IdpFunnelDTO {
  webKey: IdpWebKey
  steps?: Array<{
    stepIndex: number
    stepKey: string
    label: string
    users: number
    conversionRate: number
    dropOffRate: number
  }>
}

export interface IdpHeatmapDTO {
  webKey: IdpWebKey
  pageId: number
  pageUrl?: string
  deviceBucket?: string
  clicks?: Array<{
    elementId?: number
    elementSelector?: string
    elementText?: string
    xPct?: number
    yPct?: number
    clicks: number
    uniqueUsers: number
  }>
  scrollBuckets?: Array<{ bucket: number; users: number }>
}

export interface IdpDataHealthDTO {
  webKey: IdpWebKey
  healthy: boolean
  kpis?: Array<{ name: string; status: string; value: number; target: number }>
  rollupLags?: Array<{ tableName: string; watermarkTime?: number; lagSeconds?: number; status: string }>
  fieldFillRates?: Array<{ fieldName: string; fillRate: number; status: string }>
}

interface IdpBaseParams {
  webKey: IdpWebKey
  from?: string
  to?: string
}

function cleanParams(params: Record<string, any>) {
  return Object.keys(params).reduce((acc, key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, any>)
}

function serializeRepeatedParams(params: Record<string, any>) {
  const search = new URLSearchParams()
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (Array.isArray(value)) {
      value.forEach(item => search.append(key, String(item)))
      return
    }
    search.append(key, String(value))
  })
  return search.toString()
}

export function getIdpOverview(params: IdpBaseParams): Promise<IdpOverviewDTO> {
  return fetch.get('/idp/analytics/overview', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpSessions(params: IdpBaseParams & {
  filters?: Record<string, any>
  cursor?: string
  sessionId?: string
}): Promise<IdpSessionListDTO> {
  return fetch.get('/idp/analytics/sessions', {
    params: cleanParams({
      ...params,
      filters: params.filters ? JSON.stringify(params.filters) : undefined,
    }),
    noLoading: true,
  })
}

export function getIdpFunnel(params: IdpBaseParams & { steps?: string[] }): Promise<IdpFunnelDTO> {
  return fetch.get('/idp/analytics/funnel', {
    params: cleanParams(params),
    paramsSerializer: serializeRepeatedParams,
    noLoading: true,
  })
}

export function getIdpHeatmap(params: {
  webKey: IdpWebKey
  pageId: number
  deviceBucket?: string
}): Promise<IdpHeatmapDTO> {
  return fetch.get('/idp/analytics/heatmap', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpRealtime(params: {
  webKey: IdpWebKey
  limit?: number
}): Promise<IdpRealtimeEventDTO[]> {
  return fetch.get('/idp/analytics/realtime', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpHealth(params: { webKey: IdpWebKey }): Promise<IdpDataHealthDTO> {
  return fetch.get('/idp/health/snapshot', {
    params,
    noLoading: true,
  })
}
